export type MobileSurface = "explorer" | "changes" | "context" | "desktop" | "file" | "terminal";
export type MobileSidebarSurface = Extract<MobileSurface, "explorer" | "changes" | "context" | "desktop">;

const MOBILE_SURFACES = new Set<MobileSurface>([
  "explorer",
  "changes",
  "context",
  "desktop",
  "file",
  "terminal",
]);

export function normalizeMobileSurface(value: unknown): MobileSurface | undefined {
  if (typeof value !== "string") return;
  return MOBILE_SURFACES.has(value as MobileSurface) ? (value as MobileSurface) : undefined;
}

export function isMobileSidebarSurface(value: MobileSurface | undefined): value is MobileSidebarSurface {
  return value === "explorer" || value === "changes" || value === "context" || value === "desktop";
}

export function shouldHideMobileDashboard(input: {
  fleetPage: boolean;
  workspaceChatPath: boolean;
}) {
  return input.fleetPage || input.workspaceChatPath;
}

export function stripMobileSurface(route: string) {
  const hashIndex = route.indexOf("#");
  const hash = hashIndex === -1 ? "" : route.slice(hashIndex);
  const beforeHash = hashIndex === -1 ? route : route.slice(0, hashIndex);
  const queryIndex = beforeHash.indexOf("?");
  if (queryIndex === -1) return route;

  const pathname = beforeHash.slice(0, queryIndex);
  const query = beforeHash
    .slice(queryIndex + 1)
    .split("&")
    .filter(Boolean)
    .filter((part) => {
      const rawKey = part.slice(0, part.indexOf("=") === -1 ? part.length : part.indexOf("="));
      try {
        return decodeURIComponent(rawKey.replace(/\+/g, " ")) !== "surface";
      } catch {
        return rawKey !== "surface";
      }
    })
    .join("&");

  return `${pathname}${query ? `?${query}` : ""}${hash}`;
}

export type ControlRouteShell = "workspace";
export type WorkspaceRouteKind = "control" | "machine-home" | "workspace";

const CONTROL_ROUTE_PATHS = new Set([
  "/dashboard/machines",
  "/dashboard/providers",
  "/dashboard/usage",
  "/dashboard/automations",
  "/dashboard/tools",
  "/dashboard/settings",
]);

function normalizePathname(pathname: string) {
  if (pathname.length <= 1) return pathname;
  return pathname.replace(/\/+$/, "");
}

export function isControlRoutePath(pathname: string) {
  return CONTROL_ROUTE_PATHS.has(normalizePathname(pathname));
}

export function workspaceRouteKind(pathname: string): WorkspaceRouteKind {
  const normalized = normalizePathname(pathname);
  if (isControlRoutePath(normalized)) return "control";

  const segments = normalized.split("/").filter(Boolean);
  const offset = segments[0] === "dashboard" ? 1 : 0;
  if (
    segments[offset] === "m" &&
    !!segments[offset + 1] &&
    segments.length === offset + 2
  ) {
    return "machine-home";
  }

  return "workspace";
}

export function controlRouteShell(_pathname: string, _mobileLayout: boolean): ControlRouteShell {
  return "workspace";
}
